import { useCallback, useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { Button, Input } from '@highlight/ui'
import { subscribeBroadcast } from '@highlight/ipc'
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import * as Sentry from '@sentry/electron/renderer'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import { OVERLAY_FIND_IN_PAGE_RESULT_TOPIC, type OverlayFindInPageResult } from '@shared/sidepanel/find-in-page'
import { isMac } from '@shared/utils/platform'
import { useSetSidepanelFindInPageOpen, useSidepanelFindInPageOpen, useSidepanelHeaderHeight } from '../../atoms'

interface FindResultState {
  activeMatchOrdinal: number
  matches: number
}

interface SidepanelFindInPageProps {
  /** Key of the mounted view entry — re-runs the search when navigation swaps the content under it. */
  viewKey: number
}

const EMPTY_RESULT: FindResultState = {
  activeMatchOrdinal: 0,
  matches: 0,
}

/** Wait for a typing pause before hitting the native finder. */
const SEARCH_DEBOUNCE_MS = 250

export function SidepanelFindInPage({ viewKey }: SidepanelFindInPageProps) {
  const open = useSidepanelFindInPageOpen()
  const setOpen = useSetSidepanelFindInPageOpen()
  const headerHeight = useSidepanelHeaderHeight()
  const [query, setQuery] = useState('')
  const [result, setResult] = useState<FindResultState>(EMPTY_RESULT)
  // While a native scan is in flight the input's rendered text is masked to
  // bullets via -webkit-text-security so Chromium's find buffer can't match the
  // query against the query field itself. The value and caret are never
  // touched, and the visible text always comes from the mirror span below, so
  // the toggle is invisible to the user.
  const [scanning, setScanning] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const findBarRef = useRef<HTMLDivElement>(null)
  const nextSessionIdRef = useRef(0)
  const activeSessionIdRef = useRef<number | null>(null)
  const lastPointerDownAtRef = useRef(0)
  const resyncGenerationRef = useRef(0)

  // The single focus repair for Chromium's find side effects, which can
  // (a) pull focus into another text control when a match activates there,
  // (b) blur the input to nowhere, or (c) — the nasty one — leave the input
  // *looking* focused (activeElement set, keydowns land) while find teardown
  // (stopping a session, clearing a no-match scan) restored focus inside Blink
  // without re-announcing the editable state to the browser process. In that
  // poisoned state macOS never generates character events, so typing goes dead
  // until a real focus transition happens. A plain focus() is a no-op on an
  // already-focused element, so force blur→focus to re-send the state — it's
  // synchronous, so value and caret survive and nothing can interleave.
  //
  // This only runs milliseconds after find activity initiated from the input,
  // so any focus move in that window without a recent pointerdown is
  // Chromium's doing, not the user's — reclaim unconditionally.
  const resyncInputFocus = useCallback(() => {
    const generation = ++resyncGenerationRef.current
    const resync = () => {
      if (generation !== resyncGenerationRef.current) {
        return
      }
      const input = inputRef.current
      if (!input || Date.now() - lastPointerDownAtRef.current < 500) {
        return
      }
      if (document.activeElement === input) {
        input.blur()
      }
      input.focus({ preventScroll: true })
    }
    requestAnimationFrame(resync)
    // The browser→renderer find-teardown message races the first pass, so
    // repair again once it has certainly landed. A redundant cycle is a no-op
    // visually (value and caret survive a synchronous blur→focus).
    setTimeout(resync, 150)
  }, [])

  const stopNativeFind = useCallback(() => {
    if (activeSessionIdRef.current === null) {
      return
    }
    activeSessionIdRef.current = null
    setScanning(false)
    // Resync only after the stop has round-tripped: Chromium's teardown is
    // what poisons the text input state, so repairing before it runs is useless.
    void OverlayIpcRenderer.stopFindInPage()
      .catch((error) => {
        Sentry.captureException(error)
      })
      .finally(() => {
        resyncInputFocus()
      })
  }, [resyncInputFocus])

  const closeFind = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  // A recent pointerdown outside the find bar is how resyncInputFocus tells a
  // deliberate focus move apart from Chromium's find internals shuffling
  // focus around. The navigation buttons intentionally retain input focus, so
  // their pointerdowns must not suppress the repair they trigger.
  useEffect(() => {
    if (!open) {
      return
    }
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && findBarRef.current?.contains(event.target)) {
        return
      }
      lastPointerDownAtRef.current = Date.now()
    }
    window.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [open])

  // Cmd+F on macOS and Ctrl+F elsewhere opens (or re-focuses) the bar; Escape
  // closes it. Capture phase so it wins over the sidepanel hotkey handler and
  // chat input listeners without swallowing macOS Ctrl+F caret navigation.
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      const findShortcut =
        (isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'f'
      if (findShortcut) {
        event.preventDefault()
        event.stopPropagation()
        setOpen(true)
        requestAnimationFrame(() => {
          inputRef.current?.focus()
          inputRef.current?.select()
        })
        return
      }

      if (open && event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeFind()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [closeFind, open, setOpen])

  useEffect(() => {
    if (!open) {
      return
    }
    return subscribeBroadcast<OverlayFindInPageResult>(OVERLAY_FIND_IN_PAGE_RESULT_TOPIC, (nextResult) => {
      if (nextResult.sessionId !== activeSessionIdRef.current) {
        return
      }
      setResult({
        activeMatchOrdinal: nextResult.activeMatchOrdinal,
        matches: nextResult.matches,
      })
      if (nextResult.finalUpdate) {
        setScanning(false)
      }
      // A match landing in another text control can pull focus outright, and a
      // no-match scan can poison the browser-side text input state — the
      // blur→focus cycle repairs both. Results only arrive right after we
      // issue a request, so this can't fight an intentional focus move.
      resyncInputFocus()
    })
  }, [open, resyncInputFocus])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setResult(EMPTY_RESULT)
      stopNativeFind()
      return
    }

    inputRef.current?.focus()
    inputRef.current?.select()
  }, [open, stopNativeFind])

  // Debounced search: each settled query starts a fresh native find session.
  useEffect(() => {
    if (!open) {
      return
    }
    if (!query) {
      setResult(EMPTY_RESULT)
      stopNativeFind()
      return
    }

    const timeoutId = setTimeout(() => {
      const sessionId = ++nextSessionIdRef.current
      activeSessionIdRef.current = sessionId
      setScanning(true)

      const startFind = async () => {
        try {
          // findNext: true begins a new find session (Electron's naming is
          // counterintuitive); navigation passes false to advance within it.
          const started = await OverlayIpcRenderer.findInPage({
            text: query,
            sessionId,
            forward: true,
            findNext: true,
          })
          if (!started && activeSessionIdRef.current === sessionId) {
            setResult(EMPTY_RESULT)
            setScanning(false)
          }
        } catch (error) {
          if (activeSessionIdRef.current === sessionId) {
            setResult(EMPTY_RESULT)
            setScanning(false)
          }
          Sentry.captureException(error)
        }
      }

      void startFind()
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [open, query, stopNativeFind, viewKey])

  useEffect(() => {
    return () => {
      setOpen(false)
      stopNativeFind()
    }
  }, [setOpen, stopNativeFind])

  const navigate = useCallback(
    (forward: boolean) => {
      const sessionId = activeSessionIdRef.current
      if (!query || result.matches === 0 || sessionId === null) {
        return
      }

      // Blink resumes a find session from the frame selection when one exists
      // (electron#34490), and the focused input's caret counts as one that
      // window.getSelection() can't clear. The bar sits before the view
      // content in DOM order, so navigating with focus in the input restarts
      // every search at match 1. Drop pending focus repairs so they cannot
      // refocus before this find; blur and clear so Blink takes the
      // "increment active match" path. The found-in-page result refocuses
      // the input via resyncInputFocus.
      resyncGenerationRef.current += 1
      inputRef.current?.blur()
      window.getSelection()?.removeAllRanges()
      // Navigation always originates from the find bar (Enter or its own
      // buttons), so a pointerdown moments ago was on the bar itself — drop
      // the guard so the refocus isn't mistaken for stealing a user's click.
      lastPointerDownAtRef.current = 0
      setScanning(true)

      const findNext = async () => {
        try {
          await OverlayIpcRenderer.findInPage({
            text: query,
            sessionId,
            forward,
            findNext: false,
          })
        } catch (error) {
          if (activeSessionIdRef.current === sessionId) {
            setScanning(false)
            resyncInputFocus()
          }
          Sentry.captureException(error)
        }
      }
      void findNext()
    },
    [query, result.matches, resyncInputFocus],
  )

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }, [])

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // Keep keystrokes away from the sidepanel hotkey handler and the chat
      // input's type-to-focus listener.
      event.stopPropagation()
      if (event.key !== 'Enter') {
        return
      }
      event.preventDefault()
      navigate(!event.shiftKey)
    },
    [navigate],
  )

  if (!open) {
    return null
  }

  const navigationDisabled = !query || result.matches === 0

  return (
    <div
      ref={findBarRef}
      role="search"
      aria-label="Find in current view"
      data-no-drag
      className="bg-background corner-smooth pointer-events-auto absolute right-2 z-20 flex h-9 w-[292px] items-center gap-1 rounded-xl p-1 shadow-lg"
      style={{ top: headerHeight + 8 }}
    >
      <Search aria-hidden className="text-foreground-secondary ml-1 size-3.5 shrink-0" />
      <div className="relative min-w-0 flex-1">
        {/* Visible text lives in this mirror; the input's own glyphs stay
            transparent so masking them to bullets during a scan changes
            nothing on screen. */}
        <span
          aria-hidden
          data-query={query}
          className="text-foreground pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre px-1 text-sm before:content-[attr(data-query)]"
        />
        <Input
          ref={inputRef}
          autoFocus
          aria-label="Find text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={query ? undefined : 'Find in view'}
          className={twMerge(
            'caret-foreground relative h-7 min-w-0 border-0 bg-transparent px-1 py-0 text-transparent shadow-none focus-visible:border-0',
            scanning && '[-webkit-text-security:disc]',
          )}
        />
      </div>
      <span aria-live="polite" className="text-foreground-secondary type-ui-xs w-12 shrink-0 text-center tabular-nums">
        {result.activeMatchOrdinal} / {result.matches}
      </span>
      <Button
        type="button"
        variant="ghost"
        shape="square"
        aria-label="Previous match"
        disabled={navigationDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => navigate(false)}
      >
        <ChevronUp />
      </Button>
      <Button
        type="button"
        variant="ghost"
        shape="square"
        aria-label="Next match"
        disabled={navigationDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => navigate(true)}
      >
        <ChevronDown />
      </Button>
      <Button
        type="button"
        variant="ghost"
        shape="square"
        aria-label="Close find"
        onMouseDown={(event) => event.preventDefault()}
        onClick={closeFind}
      >
        <X />
      </Button>
    </div>
  )
}
